// Implements: F-UIX-009, F-UIX-007
/**
 * One device in the session table (a `tr`). Pure presentation.
 *
 * It receives a row and a handler and owns neither the request nor the
 * confirmation: the boundaries table stops a file under `components/` from
 * reaching a hook or a client, and this one has nothing it would want from
 * them (F-UIX-009).
 *
 * The current device is marked with a `Badge` that carries BOTH colour and
 * text. A coloured dot would be invisible to a screen reader and ambiguous to
 * anybody who does not already know the convention, and this is the row whose
 * revocation signs the person out — the one row where being sure matters.
 */

import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import type { SessionSummary } from "../../../types/api";
import { PLATFORM_HOUR_CYCLE, PLATFORM_LOCALE } from "../../../types/locale";
import { Badge } from "../../../shared/ui/atoms/Badge";
import { Button } from "../../../shared/ui/atoms/Button";
import { formatLocation } from "../../../shared/ui/locationText";
import styles from "./SessionRow.module.css";

/**
 * The moment, in the platform locale on the 24-hour clock (`types/locale.ts`) and the reader's
 * own zone.
 *
 * The machine-readable value stays in `dateTime`, which is what a test and an
 * assistive technology read; the visible text is for the person.
 */
const MOMENT = new Intl.DateTimeFormat(PLATFORM_LOCALE, {
  dateStyle: "medium",
  timeStyle: "short",
  hourCycle: PLATFORM_HOUR_CYCLE,
});

/**
 * The moment as text, never as an exception.
 *
 * `Intl.DateTimeFormat.format` throws `RangeError` for anything `Date` cannot
 * parse, and this list is mounted on every guarded route: one malformed field
 * in one row would blank the entire document. The raw value is shown instead,
 * which is also the string a bug report needs to quote; only a field that
 * arrived empty falls back to the dash, because there is nothing to quote.
 *
 * @param raw The wire value of `last_seen_at`, which the backend may malform.
 * @param absent What to show when there is no value at all.
 * @returns The localised moment, the raw value, or `absent`.
 */
export function momentText(raw: string | null | undefined, absent: string): string {
  const value = raw ?? "";
  if (value.trim() === "") return absent;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : MOMENT.format(parsed);
}

export interface SessionRowProps {
  readonly session: SessionSummary;
  readonly onRevoke: (session: SessionSummary) => void;
  /** A revocation is already on its way; no second one starts from here. */
  readonly disabled?: boolean | undefined;
}

export function SessionRow({ session, onRevoke, disabled = false }: SessionRowProps): ReactElement {
  const { t } = useTranslation();
  // The backend may not know the device: a session with no label is still a
  // session, and the generic noun is better than an empty cell.
  const label = session.device_label ?? t("auth.sessions.device");

  return (
    <tr className={styles.row} data-current={session.current}>
      <td>
        <span className={styles.device}>
          {label}
          {session.current ? <Badge tone="info">{t("auth.sessions.current")}</Badge> : null}
        </span>
        {/* Mockup v3 P1: the address sits under the device, the place gets its own column. */}
        <div className={styles.address} data-testid="session-ip">
          {session.client_ip ?? t("common.value.unknown")}
        </div>
      </td>
      <td data-testid="session-location">{formatLocation(session.location, t("account.location.unknown"))}</td>
      <td className={styles.moment}>
        <time dateTime={session.last_seen_at}>{momentText(session.last_seen_at, t("common.value.unknown"))}</time>
      </td>
      <td className={styles.actions}>
        {/* Mockup v3 P1: this device is closed from the user menu, never from its own row. */}
        {session.current ? null : (
          <Button
            variant="danger"
            size="sm"
            disabled={disabled}
            // One name per row (the visible "Revocar" first, WCAG 2.5.3): a list of identical
            // "Revocar" buttons does not say which device each one signs out.
            label={t("auth.sessions.revokeLabel", { device: label })}
            onClick={() => {
              onRevoke(session);
            }}
          >
            {t("auth.sessions.revoke")}
          </Button>
        )}
      </td>
    </tr>
  );
}
